// Read-only reference card for a Pipedrive source pipeline.
// Renders on the Pipeline Workflows page so Ops can see which stages live
// where in Pipedrive and how they route into Talmia 2 (Lead / Deal / Need Tier).
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronRight, GitBranch, Crown, ShieldCheck,
  Inbox,
} from "lucide-react";

const ROUTE_ICON = {
  lead: Inbox,
  deal: Crown,
};
const ROUTE_TONE = {
  lead: "text-sky-300 border-sky-500/40 bg-sky-500/[0.08]",
  deal: "text-slate-300 border-slate-500/30 bg-slate-500/[0.05]",
};

export default function PipedriveSourceCard({ pipeline }) {
  const RouteIcon = ROUTE_ICON[pipeline.route] || Crown;
  const tone = ROUTE_TONE[pipeline.route] || ROUTE_TONE.deal;
  return (
    <div
      data-testid={`pipedrive-source-${pipeline.id}`}
      className={`rounded-xl border p-4 space-y-3 ${tone}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mb-0.5 flex items-center gap-1">
            <GitBranch className="w-2.5 h-2.5" /> Pipedrive · pipeline #{pipeline.id}
          </div>
          <div className="text-white font-semibold text-sm truncate">
            {pipeline.name}
          </div>
        </div>
        <span
          data-testid={`pipedrive-source-badge-${pipeline.id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest border border-current shrink-0"
        >
          <RouteIcon className="w-2.5 h-2.5" /> {pipeline.route_badge}
        </span>
      </div>

      {/* Route arrow */}
      <div className="flex items-center gap-2 text-[11px] text-slate-400">
        <span className="font-mono">Pipedrive</span>
        <ArrowRight className="w-3 h-3 shrink-0" />
        <Link
          to={pipeline.route_target}
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-orange-300 hover:text-orange-200 truncate"
          data-testid={`pipedrive-source-target-${pipeline.id}`}
        >
          {pipeline.route_label}
        </Link>
      </div>

      <p className="text-[11.5px] text-slate-400 leading-snug">
        {pipeline.description}
      </p>

      {/* Stages list */}
      <div className="pt-2 border-t border-white/[0.06] space-y-1">
        <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mb-1">
          Stages · {pipeline.stages.length}
        </div>
        {pipeline.stages.map((s, i) => (
          <div
            key={s.id}
            data-testid={`pipedrive-source-${pipeline.id}-stage-${s.id}`}
            className="flex items-center gap-2 text-[11.5px]"
          >
            <span className="w-4 h-4 rounded border border-white/10 bg-white/[0.03] grid place-items-center text-[9px] font-mono text-slate-500 shrink-0">
              {i + 1}
            </span>
            <span className="text-slate-300 truncate">{s.name.trim()}</span>
            <span className="ml-auto text-[9.5px] font-mono uppercase tracking-widest text-slate-500 shrink-0">
              {s.canonical_key || "unmapped"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
